<template>
    <app-layout :showTrialPeriodBar="false" :isPageLoading="isLoading" :isGlobalErrorHandlerUsed="true">
        <common-page-plain>
            <div class="shared-records-root">
                <div v-if="!isLoading && !isValid" class="shared-records-message">
                    <h2>{{ invalidHeading }}</h2>
                    <p>{{ invalidMessage }}</p>
                </div>

                <div v-else-if="!isLoading && isValid && recipientMismatch" class="shared-records-message">
                    <h2>{{ $t('pages.public.shared_records.recipient_mismatch_heading') }}</h2>
                    <p>
                        {{
                            $t('pages.public.shared_records.recipient_mismatch_message', {
                                email: recipientEmail || $t('pages.public.shared_records.another_email'),
                            })
                        }}
                    </p>
                    <common-buttons align="center">
                        <common-button
                            :label="$t('pages.public.shared_records.switch_account')"
                            type="primary"
                            @click="switchAccount"
                        ></common-button>
                    </common-buttons>
                </div>

                <shared-records-access-gate
                    v-else-if="!isLoading && isValid && !hasAccess"
                    :token="token"
                    :sharedByName="sharedByName"
                    :documentCount="documentCount"
                    :requiresAccessCode="requiresAccessCode"
                    :recipientEmail="recipientEmail"
                    :view="gateView"
                    @change-view="gateView = $event"
                    @login="goToLogin"
                    @guest-verified="onGuestVerified"
                    @authenticated="onAuthenticated"
                ></shared-records-access-gate>

                <shared-records-document-list
                    v-else-if="!isLoading && isValid && hasAccess"
                    :documents="documents"
                    :token="token"
                    :guestToken="guestToken"
                    :sharedByName="sharedByName"
                    :practiceName="practiceName"
                    :expiresAt="expiresAt"
                    :loadError="loadError"
                ></shared-records-document-list>
            </div>
        </common-page-plain>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import DocumentSharesAPI from 'api/DocumentSharesAPI'
    import AuthService from 'services/AuthService'
    import SharedRecordsAccessGate from 'components/page-elements/shared-records/SharedRecordsAccessGate'
    import SharedRecordsDocumentList from 'components/page-elements/shared-records/SharedRecordsDocumentList'

    export default {
        components: {
            AppLayout,
            SharedRecordsAccessGate,
            SharedRecordsDocumentList,
        },

        data() {
            return {
                isLoading: true,
                isValid: false,
                isExpired: false,
                isRevoked: false,
                sharedByName: '',
                practiceName: '',
                documentCount: 0,
                documents: [],
                expiresAt: null,
                loadError: '',
                requiresAccessCode: false,
                recipientEmail: null,
                guestToken: null,
                gateView: 'menu',
                recipientMismatch: false,
                // AuthService.isLoggedIn() reads localStorage — not reactive; flip this after login on this page
                isAuthed: AuthService.isLoggedIn(),
            }
        },

        computed: {
            token() {
                return this.$route.params.token
            },

            hasAccess() {
                return this.isAuthed || !!this.guestToken
            },

            invalidHeading() {
                if (this.isRevoked) return this.$t('pages.public.shared_records.revoked_heading')
                if (this.isExpired) return this.$t('pages.public.shared_records.expired_heading')
                return this.$t('pages.public.shared_records.not_found_heading')
            },

            invalidMessage() {
                if (this.isRevoked) return this.$t('pages.public.shared_records.revoked_message')
                if (this.isExpired) return this.$t('pages.public.shared_records.expired_message')
                return this.$t('pages.public.shared_records.not_found_message')
            },
        },

        methods: {
            guestStorageKey() {
                return `share-guest:${this.token}`
            },

            goToLogin() {
                this.$router.push({ name: 'auth.login', query: { redirect: this.$route.fullPath } })
            },

            switchAccount() {
                Promise.resolve(AuthService.logOut()).finally(() => {
                    this.isAuthed = false
                    this.recipientMismatch = false
                    this.documents = []
                    this.goToLogin()
                })
            },

            onGuestVerified(guestToken) {
                this.guestToken = guestToken
                this.loadDocuments()
            },

            onAuthenticated() {
                this.isAuthed = true
                this.guestToken = null
                sessionStorage.removeItem(this.guestStorageKey())
                this.loadDocuments()
            },

            loadStatus() {
                return DocumentSharesAPI.getStatus(this.token).then((res) => {
                    this.isValid = res.valid
                    this.isExpired = !!res.expired
                    this.isRevoked = !!res.revoked
                    this.sharedByName = res.sharedByName || ''
                    this.practiceName = res.practiceName || ''
                    this.documentCount = res.documentCount || 0
                    this.requiresAccessCode = !!res.requiresAccessCode
                    this.recipientEmail = res.recipientEmail || null
                })
            },

            loadDocuments() {
                this.loadError = ''
                this.recipientMismatch = false

                const request = this.guestToken
                    ? DocumentSharesAPI.getAsGuest(this.token, this.guestToken)
                    : DocumentSharesAPI.get(this.token)

                return request
                    .then((res) => {
                        this.sharedByName = `${res.sharedBy.firstName} ${res.sharedBy.lastName}`.trim()
                        this.practiceName = res.practice ? res.practice.name : ''
                        this.documents = res.documents
                        this.expiresAt = res.expiresAt
                    })
                    .catch((err) => {
                        const res = err?.response || {}
                        const status = res._status
                        const subCode = res.data?.subCode
                        if (status === 403 && subCode === 'share.recipient_mismatch') {
                            this.recipientMismatch = true
                            return
                        }
                        if (this.guestToken && status === 401) {
                            sessionStorage.removeItem(this.guestStorageKey())
                            this.guestToken = null
                            this.gateView = 'guest'
                            return
                        }
                        this.loadError = res.message || this.$t('pages.public.shared_records.errors.load_failed')
                    })
            },
        },

        mounted() {
            const storedGuest = sessionStorage.getItem(this.guestStorageKey())
            if (storedGuest && !this.isAuthed) {
                this.guestToken = storedGuest
            }

            this.loadStatus()
                .then(() => {
                    if (this.isValid && this.hasAccess) {
                        return this.loadDocuments()
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
    }
</script>

<style scoped lang="scss">
    .shared-records-root {
        width: 100%;
    }

    .shared-records-message {
        text-align: center;
        padding: 40px 20px;

        p {
            margin-bottom: 32px;
        }
    }
</style>
